import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AuthImg } from "@/components/AuthImg";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { toast } from "sonner";
import { CameraOff, Images, Loader2, Star } from "lucide-react";

// ONE PHOTO PER TRUCK ON THE VEHICLES LIST, AND NOTHING HIDDEN.
//
// WHY: 50 rows x a 12-thumb strip was up to 600 thumbnail requests for one page, and the photo
// working set was every photo of every truck — more than the per-pod cache can hold, so the
// misses were permanent. One photo per row is ~50 requests and the SAME ~50 files every time, so
// the cache actually holds them. Recognition is one photo's job anyway; twelve 48-px squares of
// the same truck from the same angle do not help at that size.
//
// The count is the "nothing hidden" half: it fetches the whole set ON DEMAND and opens the
// viewer on the main photo, so the cost is paid by the person who opened them rather than by all
// 50 rows. Paging in the viewer covers every photo.
//
// `countOverlay` is the LIST shape (his 2026-10-07 note: the row had a photo AND a count chip
// that opened the same viewer — two controls for one job). The count sits ON the photo, so there
// is exactly ONE thing to tap and the row is only as tall as the photo. The floor card keeps the
// stacked shape, where a wide chip under a big photo is the right thing.
//
// Two empty states, said differently and never one generic broken icon: "No photo yet" is a
// worklist item; a photo that will not load keeps `AuthImg`'s own honest failure placeholder.
// PARTS USE THE SAME CELL (Batch 3b): `scope="part"`. A part can have photos but no main photo — only a
// tag close-up or number photos — and that says "No overall photo yet" rather than showing the tag.
export function MainPhotoCell({ scope = "donor", recordId, stockNumber, main, photoCount = 0, onChanged,
                                size = "w-16 h-16", big = false, countOverlay = false, compactCount = false,
                                testidPrefix = "main-photo-cell" }) {
  const [idx, setIdx] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [override, setOverride] = useState(null);
  const [resolved, setResolved] = useState(main?.file_id || null);
  const [busy, setBusy] = useState(false);

  const load = async (openIt) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/${scope === "part" ? "parts" : "donors"}/${recordId}/photos`, { params: { full: true } });
      const live = (data.photos || []).filter((p) => p?.file_id && !p.is_archived && !(scope === "part" && p.bytes_missing));
      setPhotos(live);
      setOverride(data.main_photo_file_id || null);
      setResolved(data.main_photo?.file_id || null);
      if (openIt) {
        const at = live.findIndex((p) => p.file_id === data.main_photo?.file_id);
        setIdx(at >= 0 ? at : 0);
      }
      return data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return null;
    } finally { setBusy(false); }
  };

  const viewer = (
    <PhotoLightbox photos={photos} index={idx} onClose={() => setIdx(null)} onIndex={setIdx}
      testidPrefix={`${testidPrefix}-lightbox`}
      fixScope={scope} fixRecordId={recordId}
      onFixed={() => { setIdx(null); onChanged?.(); }}
      mainScope={scope} mainRecordId={recordId} mainPhotoFileId={override} resolvedMainFileId={resolved}
      onMainChanged={async () => { await load(false); onChanged?.(); }} />
  );

  if (photoCount && !main?.file_id) {
    return (
      <span className="inline-flex shrink-0" data-testid={testidPrefix}>
        <button type="button" data-testid={`${testidPrefix}-no-overall`} onClick={() => load(true)} disabled={busy}
          title={`${stockNumber} has ${photoCount} photo${photoCount === 1 ? "" : "s"} but none is an overall shot — tap to see them`}
          className={`inline-flex ${countOverlay ? size : "h-9 px-2"} shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-amber-300 bg-amber-50 text-[10px] font-semibold text-amber-700 leading-tight text-center`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CameraOff className="w-4 h-4" />}
          No overall photo yet
        </button>
        {viewer}
      </span>
    );
  }

  if (!photoCount) {
    // In the list the empty state holds the SAME square, so a truck with no photo yet does not
    // make its row a different height from the one above it.
    if (countOverlay) {
      return (
        <span data-testid={`${testidPrefix}-none`} title={`${stockNumber} has no photo yet`}
          className={`inline-flex ${size} shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-[10px] font-semibold text-slate-400`}>
          <CameraOff className="w-5 h-5" /> No photo yet
        </span>
      );
    }
    return (
      <span data-testid={`${testidPrefix}-none`}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-2 font-semibold text-slate-400 ${big ? "h-11 text-sm" : "h-9 text-[11px]"}`}>
        <CameraOff className={big ? "w-4 h-4" : "w-3.5 h-3.5"} /> No photo yet
      </span>
    );
  }

  if (countOverlay) {
    return (
      <span className="inline-flex shrink-0" data-testid={testidPrefix}>
        <button type="button" data-testid={`${testidPrefix}-open`} onClick={() => load(true)} disabled={busy}
          title={`${photoCount} photo${photoCount === 1 ? "" : "s"} of ${stockNumber} — tap to see them`}
          className="relative rounded-lg overflow-hidden border border-slate-200 hover:ring-2 hover:ring-[#E0781A]/40 transition-shadow shrink-0">
          <AuthImg fileId={main.file_id} thumb className={`${size} object-cover`}
            alt={`${stockNumber} main photo`} />
          {/* Part rows: a small corner badge, so the photo itself stays recognisable at 56 px. */}
          <span data-testid={`${testidPrefix}-count`}
            className={compactCount
              ? "absolute bottom-0.5 left-0.5 inline-flex items-center gap-0.5 rounded bg-black/65 px-1 py-px text-[9px] font-bold leading-none text-white"
              : "absolute bottom-0 left-0 right-0 inline-flex items-center justify-center gap-1 bg-black/60 py-0.5 text-[10px] font-semibold text-white"}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Images className={compactCount ? "w-2.5 h-2.5" : "w-3 h-3"} />}
            {compactCount ? photoCount : `${photoCount} photo${photoCount === 1 ? "" : "s"}`}
          </span>
          {main.is_override && (
            <span data-testid={`${testidPrefix}-chosen`} title={`Somebody chose this photo for this ${scope === "part" ? "part" : "truck"}`}
              className="absolute top-0.5 right-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#E0781A] text-white">
              <Star className="w-2.5 h-2.5 fill-current" />
            </span>
          )}
        </button>
        {viewer}
      </span>
    );
  }

  return (
    <span className={`inline-flex gap-2 ${big ? "flex-col items-center" : "items-start"}`} data-testid={testidPrefix}>
      <button type="button" data-testid={`${testidPrefix}-open`} onClick={() => load(true)}
        title={`${photoCount} photo${photoCount === 1 ? "" : "s"} of ${stockNumber} — tap to see them`}
        className="relative rounded-lg overflow-hidden border border-slate-200 hover:ring-2 hover:ring-[#E0781A]/40 transition-shadow shrink-0">
        <AuthImg fileId={main.file_id} thumb className={`${size} object-cover`}
          alt={`${stockNumber} main photo`} />
        {main.is_override && (
          <span data-testid={`${testidPrefix}-chosen`} title={`Somebody chose this photo for this ${scope === "part" ? "part" : "truck"}`}
            className="absolute bottom-0.5 right-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#E0781A] text-white">
            <Star className="w-2.5 h-2.5 fill-current" />
          </span>
        )}
      </button>
      <button type="button" data-testid={`${testidPrefix}-count`} onClick={() => load(true)}
        disabled={busy}
        className={`inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 font-semibold text-slate-600 hover:border-[#E0781A] hover:text-[#C76714] ${big ? "h-11 text-xs" : "h-7 text-[10px]"}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Images className="w-3 h-3" />}
        {photoCount} photo{photoCount === 1 ? "" : "s"}
      </button>

      {viewer}
    </span>
  );
}
